import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowDownToLine, ArrowUpToLine, ChevronRight, Eye, EyeOff, Layers, Save, Scissors } from 'lucide-react'
import ColorPicker from '../ColorPicker.jsx'
import { isHolderPart } from '../../../../Editor3D/partsLayout.js'

/**
 * Handler de `onChange` para un slider que gobierna algo en Three.js.
 *
 * Arrastrar emite un evento por frame; propagar cada uno hasta la escena satura
 * en tablet. Se queda el último valor y se entrega una sola vez por frame.
 */
const useRafThrottledChange = (onChange) => {
    const pendingRef = useRef(null)
    const frameRef = useRef(null)

    const flush = useCallback(() => {
        frameRef.current = null
        if (pendingRef.current === null) return
        onChange?.(pendingRef.current)
        pendingRef.current = null
    }, [onChange])

    useEffect(() => () => {
        if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }, [])

    return useCallback((event) => {
        pendingRef.current = Number(event.target.value)
        if (frameRef.current !== null) return
        frameRef.current = requestAnimationFrame(flush)
    }, [flush])
}

// Etiqueta amigable para cada pieza: si el nombre es un color hex lo mostramos
// como "#RRGGBB"; si no, el nombre tal cual (p. ej. el cuerpo/fondo).
const HEX_NAME = /^[0-9a-fA-F]{6}$/

const partLabel = (part) => {
    // Lo que es en el lienzo 2D (el texto, "Forma"...), si la malla lo sabe.
    if (part?.objectLabel) return part.objectLabel
    const normalizedName = part?.name?.replace(/^#/, '')
    if (normalizedName && HEX_NAME.test(normalizedName)) return `#${normalizedName.toUpperCase()}`
    return part?.name || 'Pieza'
}

const partColor = (part, colors) => colors[part.key] || part.colorHex || '#888888'

/**
 * Agrupa las piezas por color actual: un grupo es un filamento. El fondo va
 * aparte aunque coincida con un dibujo, porque se pinta por separado. La clave
 * del grupo es la de su primera pieza y no el color, para que repintarlo no lo
 * pliegue.
 */
const groupPartsByColor = (parts, colors) => {
    const groups = new Map()
    parts.forEach((part) => {
        const color = partColor(part, colors).toLowerCase()
        const bucket = `${part.isBackground ? 'bg' : 'fg'}:${color}`
        if (!groups.has(bucket)) groups.set(bucket, { color, isBackground: Boolean(part.isBackground), parts: [] })
        groups.get(bucket).parts.push(part)
    })
    return [...groups.values()].map(group => ({ ...group, key: group.parts[0].key }))
}

const PartVisibilityPanel = ({
    parts = [],
    visibility = {},
    onToggle,
    onShowAll,
    onSolo,
    colors = {},
    onColorChange,
    onGroupColorChange,
    onSaveColors,
    hasColorChanges = false,
    isSavingColors = false,
    canExplode = false,
    partsLayout = 'assembled',
    onPartsLayoutChange,
    separationMm = 45,
    onSeparationChange,
    sectionEnabled = false,
    onSectionEnabledChange,
    sectionHeightPct = 50,
    onSectionHeightChange,
    sectionKeep = 'bottom',
    onSectionKeepChange,
}) => {
    // Lo que se está repintando: una pieza o un grupo entero.
    const [selection, setSelection] = useState(null)
    const [expandedGroups, setExpandedGroups] = useState({})
    const handleSeparationInput = useRafThrottledChange(onSeparationChange)
    const handleSectionHeightInput = useRafThrottledChange(onSectionHeightChange)

    if (!parts.length) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center opacity-70">
                <Layers className="h-10 w-10" />
                <p className="text-sm">
                    Aún no hay piezas que mostrar. Genera o carga el modelo 3D para
                    ver sus partes aquí.
                </p>
            </div>
        )
    }

    const renderPartRow = (part, { nested = false } = {}) => {
        const isVisible = visibility[part.id] !== false
        const label = partLabel(part)
        return (
            <li
                key={part.id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-opacity ${nested ? 'bg-base-100' : 'border border-base-300 bg-base-200'} ${isVisible ? '' : 'opacity-50'}`}
            >
                <button
                    type="button"
                    className="h-7 w-7 shrink-0 rounded-md border border-base-300 shadow-inner"
                    style={{ backgroundColor: partColor(part, colors) }}
                    title={nested ? 'Cambiar el color solo de esta pieza' : 'Cambiar color de la pieza'}
                    onClick={() => setSelection({ keys: [part.key], title: label })}
                    aria-label={`Cambiar color de ${label}`}
                />
                <span className="flex-1 truncate text-sm font-medium" title={label}>
                    {label}
                </span>

                <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => onSolo?.(part.id)}
                    title="Ver solo esta pieza"
                >
                    Solo
                </button>

                <button
                    type="button"
                    className={`btn btn-xs btn-circle ${isVisible ? 'btn-ghost' : 'btn-primary'}`}
                    onClick={() => onToggle?.(part.id)}
                    title={isVisible ? 'Ocultar pieza' : 'Mostrar pieza'}
                    aria-pressed={!isVisible}
                >
                    {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
            </li>
        )
    }

    const visibleCount = parts.filter((p) => visibility[p.id] !== false).length
    const allVisible = visibleCount === parts.length

    const isOpen = partsLayout === 'open'
    const isSeparated = partsLayout === 'separated'
    // Con la caja para posavasos lo que se mueve es la pila, no una tapa.
    const isCoasterHolder = parts.some(isHolderPart)
    const labels = isCoasterHolder
        ? { title: 'La pila', open: 'Sacar los posavasos', close: 'Meterlos en la caja',
            separate: 'Ver la caja y la pila por separado', join: 'Volver a meterlos',
            lift: 'Cuánto se sacan' }
        : { title: 'Ver el interior', open: 'Abrir la caja', close: 'Cerrar la caja',
            separate: 'Ver las piezas por separado', join: 'Volver a montarla',
            lift: 'Cuánto se levanta' }

    return (
        <div className="flex flex-col gap-3">
            {!canExplode && (
                // Sin esto, un modelo que no expone piezas frontales simplemente
                // no mostraba el control, sin decir por qué.
                <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs leading-snug">
                    Este modelo 3D no tiene piezas separables (no se encontró ninguna
                    pieza <code>front_*</code>), así que no se puede abrir. Si es una
                    caja de luz, <strong>regenera el modelo 3D</strong> para que se
                    construya en dos piezas.
                    <div className="mt-1 opacity-70">
                        Piezas detectadas: {parts.map((part) => part.name).join(', ') || '—'}
                    </div>
                </div>
            )}
            {canExplode && (
                <div className="rounded-lg border border-base-300 bg-base-200 p-3">
                    <p className="text-sm font-semibold">{labels.title}</p>

                    <button
                        type="button"
                        onClick={() => onPartsLayoutChange?.(isOpen ? 'assembled' : 'open')}
                        className={`btn btn-sm mt-2 w-full gap-2 ${isOpen ? 'btn-primary' : 'btn-outline btn-primary'}`}
                    >
                        {isOpen ? labels.close : labels.open}
                    </button>

                    <button
                        type="button"
                        title="Las dos piezas, una al lado de la otra, como se imprimen"
                        onClick={() => onPartsLayoutChange?.(isSeparated ? 'assembled' : 'separated')}
                        className={`btn btn-xs mt-2 w-full ${isSeparated ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                    >
                        {isSeparated ? labels.join : labels.separate}
                    </button>

                    {partsLayout !== 'assembled' && (
                        <div className="mt-3">
                            <div className="flex items-center justify-between text-xs opacity-70">
                                <span>{isOpen ? labels.lift : 'Separación'}</span>
                                <strong>{Math.round(separationMm)} mm</strong>
                            </div>
                            <input
                                type="range"
                                min={5}
                                max={150}
                                step={5}
                                value={separationMm}
                                onChange={handleSeparationInput}
                                className="range range-primary range-xs mt-1"
                            />
                        </div>
                    )}
                    <p className="mt-2 text-xs opacity-60">
                        Es solo una vista: no cambia el modelo ni lo que se imprime.
                    </p>
                </div>
            )}

            {/* Seccionado. Fuera del bloque de arriba a propósito: cortar no
                necesita que el modelo tenga piezas separables, así que vale
                igual para un llavero de una pieza que para una caja de luz. */}
            <div className="rounded-lg border border-base-300 bg-base-200 p-3">
                <label className="flex cursor-pointer items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                        <Scissors className="h-4 w-4" />
                        Cortar la pieza
                    </span>
                    <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={sectionEnabled}
                        onChange={(event) => onSectionEnabledChange?.(event.target.checked)}
                    />
                </label>

                {sectionEnabled && (
                    <>
                        <div className="mt-3">
                            <div className="flex items-center justify-between text-xs opacity-70">
                                <span>Altura del corte</span>
                                <strong>{Math.round(sectionHeightPct)}%</strong>
                            </div>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={sectionHeightPct}
                                onChange={handleSectionHeightInput}
                                className="range range-primary range-xs mt-1"
                            />
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => onSectionKeepChange?.('bottom')}
                                title="Quitar la parte de arriba y quedarse con la de abajo"
                                className={`btn btn-xs gap-1 ${sectionKeep === 'bottom' ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                            >
                                <ArrowDownToLine className="h-3.5 w-3.5" />
                                Parte de abajo
                            </button>
                            <button
                                type="button"
                                onClick={() => onSectionKeepChange?.('top')}
                                title="Quitar la parte de abajo y quedarse con la de arriba"
                                className={`btn btn-xs gap-1 ${sectionKeep === 'top' ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                            >
                                <ArrowUpToLine className="h-3.5 w-3.5" />
                                Parte de arriba
                            </button>
                        </div>
                    </>
                )}

                <p className="mt-2 text-xs opacity-60">
                    Es solo una vista: no cambia el modelo ni lo que se imprime.
                </p>
            </div>

            <div className="flex items-center justify-between">
                <span className="text-sm opacity-70">
                    {visibleCount}/{parts.length} piezas visibles
                </span>
                <button
                    type="button"
                    className="btn btn-xs btn-ghost border border-base-300"
                    onClick={onShowAll}
                    disabled={allVisible}
                >
                    Mostrar todas
                </button>
            </div>

            <ul className="flex flex-col gap-2">
                {groupPartsByColor(parts, colors).map((group) => {
                    if (group.parts.length === 1) {
                        return renderPartRow(group.parts[0])
                    }
                    const isExpanded = Boolean(expandedGroups[group.key])
                    const anyVisible = group.parts.some((part) => visibility[part.id] !== false)
                    const groupLabel = group.isBackground ? 'Fondo' : group.color.toUpperCase()
                    return (
                        <li key={group.key} className="rounded-lg border border-base-300 bg-base-200">
                            <div className={`flex items-center gap-2 px-2 py-2 transition-opacity ${anyVisible ? '' : 'opacity-50'}`}>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-ghost btn-square"
                                    onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.key]: !isExpanded }))}
                                    aria-expanded={isExpanded}
                                    title={isExpanded ? 'Plegar el grupo' : 'Ver las piezas del grupo'}
                                >
                                    <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                                <button
                                    type="button"
                                    className="h-7 w-7 shrink-0 rounded-md border border-base-300 shadow-inner"
                                    style={{ backgroundColor: group.color }}
                                    title="Cambiar el color de todo el grupo"
                                    onClick={() => setSelection({ keys: group.parts.map((part) => part.key), title: `todo el grupo ${groupLabel}` })}
                                    aria-label={`Cambiar color de todo el grupo ${groupLabel}`}
                                />
                                <span className="flex-1 truncate text-sm font-medium" title={groupLabel}>
                                    {groupLabel}
                                    <span className="ml-1 text-xs font-normal opacity-60">· {group.parts.length} piezas</span>
                                </span>
                                <button
                                    type="button"
                                    className={`btn btn-xs btn-circle ${anyVisible ? 'btn-ghost' : 'btn-primary'}`}
                                    onClick={() => group.parts
                                        .filter((part) => (visibility[part.id] !== false) === anyVisible)
                                        .forEach((part) => onToggle?.(part.id))}
                                    title={anyVisible ? 'Ocultar el grupo' : 'Mostrar el grupo'}
                                    aria-pressed={!anyVisible}
                                >
                                    {anyVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                            </div>
                            {isExpanded && (
                                <ul className="flex flex-col gap-1 border-t border-base-300 p-2 pl-6">
                                    {group.parts.map((part) => renderPartRow(part, { nested: true }))}
                                </ul>
                            )}
                        </li>
                    )
                })}
            </ul>

            {selection && (() => {
                const selectedParts = selection.keys
                    .map((key) => parts.find((part) => part.key === key))
                    .filter(Boolean)
                if (!selectedParts.length) return null
                return (
                    <div className="rounded-lg border border-base-300 bg-base-200 p-3">
                        <p className="mb-2 text-sm font-semibold">
                            Color de {selection.title}
                        </p>
                        <ColorPicker
                            value={partColor(selectedParts[0], colors)}
                            onChange={(color) => (selectedParts.length > 1
                                ? onGroupColorChange?.(selectedParts, color)
                                : onColorChange?.(selectedParts[0], color))}
                            allowVaciado={false}
                        />
                    </div>
                )
            })()}

            <button
                type="button"
                className="btn btn-primary mt-2 w-full"
                onClick={onSaveColors}
                disabled={!hasColorChanges || isSavingColors}
            >
                {isSavingColors
                    ? <span className="loading loading-spinner loading-sm" />
                    : <Save className="h-4 w-4" />}
                {isSavingColors ? 'Guardando...' : 'Guardar colores'}
            </button>
        </div>
    )
}

PartVisibilityPanel.propTypes = {
    parts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            key: PropTypes.string.isRequired,
            name: PropTypes.string,
            colorHex: PropTypes.string,
            sourceColor: PropTypes.string,
            isBackground: PropTypes.bool,
            objectUid: PropTypes.string,
            objectLabel: PropTypes.string,
        })
    ),
    visibility: PropTypes.object,
    onToggle: PropTypes.func,
    onShowAll: PropTypes.func,
    onSolo: PropTypes.func,
    colors: PropTypes.object,
    onColorChange: PropTypes.func,
    onGroupColorChange: PropTypes.func,
    onSaveColors: PropTypes.func,
    hasColorChanges: PropTypes.bool,
    isSavingColors: PropTypes.bool,
    canExplode: PropTypes.bool,
    partsLayout: PropTypes.oneOf(['assembled', 'open', 'separated']),
    onPartsLayoutChange: PropTypes.func,
    separationMm: PropTypes.number,
    onSeparationChange: PropTypes.func,
    sectionEnabled: PropTypes.bool,
    onSectionEnabledChange: PropTypes.func,
    sectionHeightPct: PropTypes.number,
    onSectionHeightChange: PropTypes.func,
    sectionKeep: PropTypes.oneOf(['bottom', 'top']),
    onSectionKeepChange: PropTypes.func,
}

export default PartVisibilityPanel
